import React, { useEffect, useMemo, useState } from 'react';
import { Text, Label, H4 } from '@amzn/stencil-react-components/text';
import { useTranslation } from 'react-i18next';
import { Col } from '@amzn/stencil-react-components/layout';
import { LineChart, LineType, DataPointShape, Color, LineDataSet } from '@amzn/stencil-react-components/data-visualization';
import { Flex } from '@amzn/stencil-react-components/layout';
import { NUM_SQPR_PERIODS } from '../constants/qualityConfiguration';
import moment from 'moment';
import { Spacer } from '@amzn/stencil-react-components/layout';
import { Select } from '@amzn/stencil-react-components/form';
import { useUserDataContext, useGlobalContext } from '../hooks';
import { WeeklySqprSummary, SQPRQualityError, QualityEvent } from '../types/backendService';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import { Hr } from '@amzn/stencil-react-components/layout';
import { Table } from '@amzn/stencil-react-components/table';
import { QualityEventCards } from './shared/QualityEventCards';
import { withTooltip } from '@amzn/stencil-react-components/tooltip';
import { IconQuestionCircle } from '@amzn/stencil-react-components/icons';
import { ExpectedDPMOReport } from './ExpectedDPMOReport';

const IconWithTooltip = withTooltip()(IconQuestionCircle);

export const SQPRSummaryTab = ({}) => {
  const { t } = useTranslation();
  const {
    selectedWeekQualityEvents,
    selectedWeekQualityErrors,
    changeSqprWeek,
    sqprWeeks: sqprWeeksContext,
    selectedSqprWeek,
    sqprSummaries: sqprSummariesContext,
  } = useUserDataContext();
  const { userDetails, isAdminViewEnabled } = useGlobalContext();

  const isWeeklyDataLoading =
      selectedWeekQualityErrors == undefined || selectedWeekQualityEvents == undefined;
  const qualityErrors: SQPRQualityError[] = selectedWeekQualityErrors || [];
  const qualityEvents: QualityEvent[] = selectedWeekQualityEvents || [];

  // All weeks to display in the week dropdown
  const sqprWeeks = sqprWeeksContext || [];

  const isSqprSummariesLoading = sqprSummariesContext == undefined;
  const sqprSummaries: WeeklySqprSummary[] = sqprSummariesContext || [];

  // Configuration of the columns for the tables breaking down weekly SQPR quality errors data.
  const sqprDetailsTableColumns = [
    {
      header: <>{t('errorFamily')} <IconWithTooltip tooltipText={t('errorFamilyExplanation')} /></>,
      accessor: 'lcFamilyId'
    },
    {
      header: <>{t('errorType')} <IconWithTooltip tooltipText={t('errorTypeExplanation')} /></>,
      accessor: ({ data }: { data: SQPRQualityError }) => data.errorType == "null" ? '-' : data.errorType
    },
    {
      header: <>{t('unitsProcessed')} <IconWithTooltip tooltipText={t('unitsProcessedExplanation')} /></>,
      accessor: 'unitsProcessed'
    },
    {
      header: <>{t('errors')} <IconWithTooltip tooltipText={t('qualityErrorsExplanation')} /></>,
      accessor: 'qualityEvents'
    },
  ];

  // Some columns are required to be feature gated.
  const featureGatedTableColumns = () => {
    sqprDetailsTableColumns.push({
      header: <>{t('expectedDPMO')} <IconWithTooltip tooltipText={t('expectedDpmoExplanation')} /></>,
      accessor: 'expectedDpmo',
    });
    return sqprDetailsTableColumns;
  };

  /**
   * Chart displaying SQPR data over a series of weeks.
   */
  const sqprSummariesChart = useMemo(() => {
    if (sqprSummaries.length == 0) {
      return <></>;
    }

    /**
     * We organize the weekly data in a map so we can efficiently look it up within 'tooltipCustomDetails'
     * The data is keyed by the day of the end of the SQPR week.
     */
    type weekData = {
      x: number,
      y: number,
      label: string,
      qualityEvents: string,
      actualDpmo: string,
      expectedDpmo: string,
      rank: string,
      percentile: string,
    };

    let minPerformance = 0;

    const applicableWeeks: Map<number, weekData> = new Map<number, weekData>();
    for (const item of sqprSummaries) {
      if (item.percentile != undefined) {
        const performance = Math.round(item.performance!);
        if (performance < minPerformance) {
          minPerformance = performance;
        }

        const value = {
          x: item.endTime!,
          y: performance,
          label: performance.toString(),
          qualityEvents: item.qualityEvents!.toString(),
          actualDpmo: Math.round(item.currentDPMO!).toString(),
          expectedDpmo: Math.round(item.expectedDPMO!).toString(),
          rank: `${item.rank}/${item.totalPopulation}`,
          percentile: Math.round(item.percentile).toString(),
        }
        applicableWeeks.set(value.x!, value);
      }
    }

    /**
     * We want to display a value on the x axis for each of the last NUM_SQPR_PERIODS weeks
     * even if the user does not have SQPR data for that week.
     */
    const currentMoment = moment().tz(userDetails.locale);
    /**
     * Note: startOf('week') returns a Sunday, so we shift by 3 days to get the wednesday of the last SQPR period
     * that has already ended.
     */
    const lastSqprPeriodEnd = currentMoment.subtract(3, 'days').startOf('week').add(3, 'days');
    let tickValues: number[] = [];
    for (let weekLookback = 0; weekLookback < NUM_SQPR_PERIODS; weekLookback++) {
      tickValues.unshift(lastSqprPeriodEnd.clone().subtract(weekLookback, 'weeks').unix());
    }

    const dataSets: LineDataSet[] = [
      {
        lineType: LineType.Solid,
        dataPointShape: DataPointShape.Circle,
        label: t('performance'),
        color: Color.Primary,
        data: Array.from(applicableWeeks.values()),
      }
    ]

    return (
      <LineChart
        dataSets={dataSets}
        tooltipCustomDetails={({ x }) =>
          {
            let details = [];

            const actualDpmo = applicableWeeks.get(x as number)!.actualDpmo;
            /**
             * Only show percentile and rank if DPMO isn't 0 since otherwise it
             * doesn't have much meaning due to there often being a many-way
             * tie in rank and percentile among associates who have a 0 DPMO.
             */
            if (actualDpmo != undefined && actualDpmo != '0') {
              details.push({
                label: t('percentile'),
                value: applicableWeeks.get(x as number)!.percentile,
              });
            }

            details.push({
              label: t('actualDPMO'),
              value: applicableWeeks.get(x as number)!.actualDpmo,
            });
            details.push({
              label: t('expectedDPMO'),
              value: applicableWeeks.get(x as number)!.expectedDpmo,
            });
            details.push({
              label: t('qualityEvents'),
              value: applicableWeeks.get(x as number)!.qualityEvents,
            });

            return details;
          }
        }
        tooltipFormatter={({ x, y }: any) => ({ x: moment(x * 1000).format("M/D/Y"), y: y })}
        height={300}
        shouldFixLabelOverlap={true}
        showBaselineAxis={true}
        showCrosshair={true}
        showDataPoints={true}
        showDependentAxis={true}
        showDependentAxisGridLines={true}
        showLabels={true}
        showLegend={true}
        showTooltip={true}
        baselineAxisTickValues={tickValues}
        dependentAxisTickValues={[minPerformance, 50, 100]}
        baselineAxisFormatter={tick => moment(tick * 1000).format("M/D/Y")}
        chartPadding={32}
      />
    );
  }, [sqprSummaries]);

  // Content to display a card for each quality event
  const qualityEventsCards = useMemo(() => {
    return (
      <QualityEventCards qualityEvents={qualityEvents} />
    );
  }, [qualityEvents]);

  // Logic to add loading spinners when appropriate
  let weeklyDataContent;
  if (isWeeklyDataLoading) {
    weeklyDataContent = (
      <>
        <Spacer height={16}/>
        <Flex flexDirection="row" justifyContent="center" alignItems="center">
          <Spinner showText loadingText={t('loadingSqprData')} />
        </Flex>
      </>
    );
  } else if (qualityErrors.length == 0 && qualityEvents.length == 0) {
    weeklyDataContent = (
      <>
        <Spacer height={16}/>
        <Text>{t('noSqprDataForWeek')}</Text>
      </>
    );
  } else {
    weeklyDataContent = (
      <>
        {qualityErrors.length > 0 &&
          <>
            <Spacer height={16}/>
            <Col>
              <Table
                aria-labelledby="sqpr-breakdown-table-label"
                columns={featureGatedTableColumns()}
                data={qualityErrors}
              />
            </Col>
          </>
        }

        {qualityEvents.length > 0 &&
          <>
            <Spacer height={16}/>
            {qualityEventsCards}
          </>
        }
      </>
    );
  }

  const thresholdDPMOContent = (
    <>
      <Spacer height={16}/>
      <Text fontWeight="light">{t('thresholdDpmo')}</Text>
      <Hr size="wide" />
      <Spacer height={16}/>
      <ExpectedDPMOReport />
    </>
  );

  if (isSqprSummariesLoading) {
    return (
      <Flex flexDirection="row" justifyContent="center" alignItems="center">
        <Spinner showText loadingText={t('loadingSqprData')} />
      </Flex>
    );
  } else if (sqprSummaries.length == 0) {
    return (
      <>
        <Text>{t('noSqprData', {weeks: NUM_SQPR_PERIODS})}</Text>

        {thresholdDPMOContent}
      </>
    );
  } else {
    return (
      <>
        <Text fontWeight="light">
          {t('sqprSectionIntro', { periods: NUM_SQPR_PERIODS })}
          <IconWithTooltip tooltipText={t('qualityPerformanceExplanation')} />
        </Text>

        <Spacer height={16}/>

        {sqprSummariesChart}

        {thresholdDPMOContent}

        <Spacer height={16}/>
        <Text fontWeight="light">{t('sqprWeeklyHeading')}</Text>
        <Hr size="wide" />

        <Spacer height={16}/>
        <Flex flexDirection="row" justifyContent="right" alignItems="center" gridGap="S300">
          <Col>
            <Label htmlFor="week-selector" id="week-selector-label">
              <Text fontSize="T300" fontWeight="medium" textAlign="right">
                {t('week')}
              </Text>
            </Label>
          </Col>
          <Col width={150}>
            <Select
              labelId="week-selector-label"
              id="week-selector"
              options={sqprWeeks}
              value={selectedSqprWeek}
              onChange={changeSqprWeek}
              renderOption={option => option.format("M/D/Y")}
            />
          </Col>
        </Flex>

        {weeklyDataContent}
      </>
    );
  }
}
